import React, { useState, useEffect, useContext, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { KeycloakContext } from '../App';
import { API_URL } from '../runtimeConfig';
import { useToast } from '../providers/ToastProvider';
import { useConfirm } from '../providers/ConfirmProvider';
import { PageHeader } from '../components/ui/page-header';
import { Skeleton } from '../components/ui/skeleton';
import { formatDate } from '../lib/format';
import {
  DocumentIcon,
  FolderIcon,
  TrashIcon,
  PencilIcon,
  ArrowUpTrayIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';

interface FileItem {
  path: string;
  size?: number;
  updated_at: string;
  is_dir?: boolean;
}

interface Website {
  id: string;
  name: string;
  published: boolean;
}

function formatFileSize(bytes?: number): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(name: string) {
  const ext = name.split('.').pop()?.toLowerCase();
  if (['html', 'htm'].includes(ext || '')) return <DocumentIcon className="h-5 w-5 text-orange-400 flex-shrink-0" />;
  if (['css'].includes(ext || '')) return <DocumentIcon className="h-5 w-5 text-blue-400 flex-shrink-0" />;
  if (['js', 'ts', 'jsx', 'tsx'].includes(ext || '')) return <DocumentIcon className="h-5 w-5 text-yellow-400 flex-shrink-0" />;
  if (['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp'].includes(ext || '')) return <DocumentIcon className="h-5 w-5 text-purple-400 flex-shrink-0" />;
  return <DocumentIcon className="h-5 w-5 text-muted-foreground flex-shrink-0" />;
}

export default function WebsiteFiles() {
  const { websiteId } = useParams<{ websiteId: string }>();
  const navigate = useNavigate();
  const ctx = useContext(KeycloakContext);
  const keycloak = ctx?.keycloak;
  const { toast } = useToast();
  const { confirm } = useConfirm();

  const [website, setWebsite] = useState<Website | null>(null);
  const [files, setFiles] = useState<FileItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPath, setCurrentPath] = useState<string>('');
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [showRedeployPrompt, setShowRedeployPrompt] = useState<boolean>(false);
  const [showRedeployModal, setShowRedeployModal] = useState<boolean>(false);
  const [redeployStatus, setRedeployStatus] = useState<string>('');
  const [redeployElapsed, setRedeployElapsed] = useState<number | null>(null);
  const [showCreateFileModal, setShowCreateFileModal] = useState<boolean>(false);
  const [newFilePath, setNewFilePath] = useState<string>('');
  const [creatingFile, setCreatingFile] = useState<boolean>(false);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState<boolean>(false);
  const [newFolderName, setNewFolderName] = useState<string>('');
  const [creatingFolder, setCreatingFolder] = useState<boolean>(false);
  const [showRenameFolderModal, setShowRenameFolderModal] = useState<boolean>(false);
  const [folderToRename, setFolderToRename] = useState<string | null>(null);
  const [newFolderNameInput, setNewFolderNameInput] = useState<string>('');
  const [renamingFolder, setRenamingFolder] = useState<boolean>(false);
  const [dragItem, setDragItem] = useState<{ type: 'file' | 'folder'; path: string } | null>(null);
  const [isDraggingFiles, setIsDraggingFiles] = useState<boolean>(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (websiteId) fetchWebsiteAndFiles();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websiteId]);

  const fetchWebsiteAndFiles = async () => {
    try {
      setLoading(true);
      const [wRes, fRes] = await Promise.all([
        fetch(`${API_URL}/websites/${websiteId}`, { headers: { Authorization: `Bearer ${keycloak?.token}` } }),
        fetch(`${API_URL}/websites/${websiteId}/files`, { headers: { Authorization: `Bearer ${keycloak?.token}` } }),
      ]);
      if (!wRes.ok) throw new Error('Failed to fetch website');
      if (!fRes.ok) throw new Error('Failed to fetch files');
      const wData = await wRes.json();
      const fData = await fRes.json();
      setWebsite(wData);
      setFiles(fData || []);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Derived directory contents from files
  const currentItems = React.useMemo(() => {
    const prefix = currentPath ? `${currentPath}/` : '';
    const dirs = new Set<string>();
    const fileItems: FileItem[] = [];
    for (const f of files) {
      if (!f.path.startsWith(prefix)) continue;
      const rel = f.path.slice(prefix.length);
      const parts = rel.split('/');
      if (parts.length > 1) {
        dirs.add(parts[0]);
      } else if (parts[0]) {
        fileItems.push(f);
      }
    }
    return {
      dirs: [...dirs].sort(),
      files: fileItems
        .filter(f => (f.path.split('/').pop() || '') !== '.keep')
        .sort((a, b) => a.path.localeCompare(b.path)),
    };
  }, [files, currentPath]);

  const breadcrumbs = React.useMemo(() => {
    if (!currentPath) return [];
    return currentPath.split('/');
  }, [currentPath]);

  const handleNavigate = (dirName: string) => {
    setCurrentPath(currentPath ? `${currentPath}/${dirName}` : dirName);
  };

  const handleGoUp = () => {
    const parts = currentPath.split('/');
    parts.pop();
    setCurrentPath(parts.join('/'));
  };

  const handleEditFile = (filePath: string) => {
    navigate(`/websites/${websiteId}/editor/${encodeURIComponent(filePath)}`);
  };

  const handleDeleteFile = async (filePath: string) => {
    const ok = await confirm({ title: 'Delete file', message: `Delete ${filePath}?`, confirmLabel: 'Delete', variant: 'danger' });
    if (!ok) return;
    try {
      const res = await fetch(`${API_URL}/websites/${websiteId}/files/${encodeURIComponent(filePath)}`, { method: 'DELETE', headers: { Authorization: `Bearer ${keycloak?.token}` } });
      if (!res.ok) throw new Error('Failed to delete file');
      setShowRedeployPrompt(true);
      await fetchWebsiteAndFiles();
    } catch (err: any) {
      toast(err.message, 'error');
    }
  };

  const handleDeleteFolder = async (folderName: string) => {
    const folderPath = currentPath ? `${currentPath}/${folderName}` : folderName;
    const ok = await confirm({ title: 'Delete folder', message: `Delete folder ${folderPath} and all its contents?`, confirmLabel: 'Delete', variant: 'danger' });
    if (!ok) return;
    try {
      const res = await fetch(`${API_URL}/websites/${websiteId}/folders/${encodeURIComponent(folderPath)}`, { method: 'DELETE', headers: { Authorization: `Bearer ${keycloak?.token}` } });
      if (!res.ok) throw new Error('Failed to delete folder');
      setShowRedeployPrompt(true);
      await fetchWebsiteAndFiles();
    } catch (err: any) {
      toast(err.message, 'error');
    }
  };

  const handleCreateFile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFilePath.trim()) return;
    setCreatingFile(true);
    try {
      const fullPath = currentPath ? `${currentPath}/${newFilePath.trim()}` : newFilePath.trim();
      const res = await fetch(`${API_URL}/websites/${websiteId}/files`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${keycloak?.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: fullPath, content: '' }),
      });
      if (!res.ok) throw new Error('Failed to create file');
      setShowCreateFileModal(false);
      setNewFilePath('');
      setShowRedeployPrompt(true);
      await fetchWebsiteAndFiles();
      handleEditFile(fullPath);
    } catch (err: any) {
      toast(err.message, 'error');
    } finally {
      setCreatingFile(false);
    }
  };

  const handleCreateFolder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFolderName.trim()) return;
    const folderName = newFolderName.trim().replace(/\/+$/g, '');
    if (!folderName) return;
    setCreatingFolder(true);
    try {
      const basePath = currentPath ? `${currentPath}/` : '';
      const placeholderPath = `${basePath}${folderName}/.keep`;
      const res = await fetch(`${API_URL}/websites/${websiteId}/files`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${keycloak?.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: placeholderPath, content: '' }),
      });
      if (!res.ok) throw new Error('Failed to create folder');
      setShowCreateFolderModal(false);
      setNewFolderName('');
      await fetchWebsiteAndFiles();
    } catch (err: any) {
      toast(err.message, 'error');
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleRenameFolder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!folderToRename || !newFolderNameInput.trim()) return;
    setRenamingFolder(true);
    try {
      const oldPath = currentPath ? `${currentPath}/${folderToRename}` : folderToRename;
      const newPath = currentPath ? `${currentPath}/${newFolderNameInput.trim()}` : newFolderNameInput.trim();
      const res = await fetch(`${API_URL}/websites/${websiteId}/files/move`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${keycloak?.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ from_path: oldPath, to_path: newPath, is_folder: true }),
      });
      if (!res.ok) throw new Error('Failed to rename folder');
      setShowRenameFolderModal(false);
      setFolderToRename(null);
      setNewFolderNameInput('');
      await fetchWebsiteAndFiles();
    } catch (err: any) {
      toast(err.message, 'error');
    } finally {
      setRenamingFolder(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    setUploadProgress(0);
    try {
      for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i];
        const filePath = currentPath ? `${currentPath}/${file.name}` : file.name;
        const content = await readFileAsBase64(file);
        await fetch(`${API_URL}/websites/${websiteId}/files`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${keycloak?.token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ path: filePath, content, encoding: 'base64' }),
        });
        setUploadProgress(Math.round(((i + 1) / fileList.length) * 100));
      }
      setShowRedeployPrompt(true);
      await fetchWebsiteAndFiles();
    } catch (err: any) {
      toast(err.message, 'error');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (event.target) event.target.value = '';
    }
  };

  const readFileAsBase64 = (file: File): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1] || result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleFolderUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    setUploadProgress(0);
    try {
      for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i];
        const relativePath = (file as any).webkitRelativePath || file.name;
        const filePath = currentPath ? `${currentPath}/${relativePath}` : relativePath;
        const content = await readFileAsBase64(file);
        await fetch(`${API_URL}/websites/${websiteId}/files`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${keycloak?.token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ path: filePath, content, encoding: 'base64' }),
        });
        setUploadProgress(Math.round(((i + 1) / fileList.length) * 100));
      }
      setShowRedeployPrompt(true);
      await fetchWebsiteAndFiles();
    } catch (err: any) {
      toast(err.message, 'error');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (event.target) event.target.value = '';
    }
  };

  const handlePageDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (e.dataTransfer.types.includes('Files') && !dragItem) {
      e.preventDefault();
      e.stopPropagation();
      setIsDraggingFiles(true);
    }
  };

  const handlePageDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDraggingFiles(false);
    }
  };

  const handlePageDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFiles(false);
    if (dragItem) return;

    const readEntry = async (entry: any, pathPrefix: string): Promise<{ path: string; file: File }[]> => {
      if (entry.isFile) {
        return new Promise((resolve) => {
          entry.file((file: File) => resolve([{ path: pathPrefix + entry.name, file }]));
        });
      } else if (entry.isDirectory) {
        const dirReader = entry.createReader();
        const allEntries: any[] = [];
        const readBatch = (): Promise<void> => new Promise((resolve) => {
          dirReader.readEntries(async (entries: any[]) => {
            if (entries.length === 0) { resolve(); return; }
            allEntries.push(...entries);
            await readBatch();
            resolve();
          });
        });
        await readBatch();
        const results: { path: string; file: File }[] = [];
        for (const child of allEntries) {
          results.push(...await readEntry(child, pathPrefix + entry.name + '/'));
        }
        return results;
      }
      return [];
    };

    const allFiles: { path: string; file: File }[] = [];
    for (const item of Array.from(e.dataTransfer.items)) {
      if (item.kind === 'file') {
        const entry = (item as any).webkitGetAsEntry?.();
        if (entry) allFiles.push(...await readEntry(entry, currentPath ? currentPath + '/' : ''));
      }
    }
    if (allFiles.length === 0) return;

    setUploading(true);
    setUploadProgress(0);
    try {
      for (let i = 0; i < allFiles.length; i++) {
        const { path, file } = allFiles[i];
        const content = await readFileAsBase64(file);
        await fetch(`${API_URL}/websites/${websiteId}/files`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${keycloak?.token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ path, content, encoding: 'base64' }),
        });
        setUploadProgress(Math.round(((i + 1) / allFiles.length) * 100));
      }
      setShowRedeployPrompt(true);
      await fetchWebsiteAndFiles();
    } catch (err: any) {
      toast(err.message, 'error');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const moveItem = async (fromPath: string, toPath: string, isFolder: boolean) => {
    try {
      const res = await fetch(`${API_URL}/websites/${websiteId}/files/move`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${keycloak?.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ from_path: fromPath, to_path: toPath, is_folder: isFolder }),
      });
      if (!res.ok) throw new Error('Failed to move item');
      setShowRedeployPrompt(true);
      await fetchWebsiteAndFiles();
    } catch (err: any) {
      toast(err.message, 'error');
    } finally {
      setDragItem(null);
    }
  };

  const handleDrop = async (e: React.DragEvent<HTMLElement>, targetAbsPath: string) => {
    e.preventDefault();
    if (!dragItem) return;
    const fromPath = dragItem.path;
    const name = fromPath.split('/').pop() || fromPath;
    const toPath = targetAbsPath ? `${targetAbsPath}/${name}` : name;
    if (toPath === fromPath) { setDragItem(null); return; }
    if (dragItem.type === 'folder' && (targetAbsPath === fromPath || targetAbsPath.startsWith(`${fromPath}/`))) {
      toast('Cannot move a folder into itself or its subfolder', 'error');
      setDragItem(null);
      return;
    }
    await moveItem(fromPath, toPath, dragItem.type === 'folder');
  };

  const handleRedeploy = async () => {
    setShowRedeployPrompt(false);
    setShowRedeployModal(true);
    setRedeployStatus('running');
    setRedeployElapsed(null);
    const startTime = Date.now();
    try {
      const res = await fetch(`${API_URL}/websites/${websiteId}/redeploy`, { method: 'POST', headers: { Authorization: `Bearer ${keycloak?.token}` } });
      if (res.status === 404) throw new Error('Website has not been published yet. Please publish it first from the Websites page before redeploying.');
      if (!res.ok) { const body = await res.json().catch(() => ({})); throw new Error(body.error || 'Deploy failed'); }
      setRedeployStatus('completed');
      setRedeployElapsed(Date.now() - startTime);
    } catch (err: any) {
      setRedeployStatus('error');
      setTimeout(() => { setShowRedeployModal(false); toast(err.message, 'error'); }, 800);
      return;
    }
    setTimeout(() => setShowRedeployModal(false), 3000);
  };

  if (loading) return (
    <div className="space-y-6">
      <PageHeader
        icon={<FolderIcon className="h-5 w-5" />}
        title={website?.name || 'Files'}
        description="Manage your website files"
      />
      <div className="rounded-lg border border-border bg-card p-6 space-y-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    </div>
  );
  if (error) return <div className="rounded-md bg-destructive/10 p-4"><p className="text-sm text-destructive">Error: {error}</p></div>;

  return (
    <div
      className={`space-y-6 relative transition-all ${isDraggingFiles ? 'after:absolute after:inset-0 after:rounded-lg after:border-2 after:border-dashed after:border-primary after:bg-primary/5 after:z-10 after:pointer-events-none' : ''}`}
      onDragOver={handlePageDragOver}
      onDragLeave={handlePageDragLeave}
      onDrop={handlePageDrop}
    >
      {isDraggingFiles && (
        <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none">
          <div className="bg-card rounded-xl shadow-lg px-8 py-6 text-center border-2 border-primary">
            <ArrowUpTrayIcon className="h-10 w-10 text-primary mx-auto mb-2" />
            <p className="text-lg font-semibold text-primary">Drop files or folders here</p>
            <p className="text-sm text-muted-foreground mt-1">They will be uploaded to the current directory</p>
          </div>
        </div>
      )}
      <PageHeader
        icon={<FolderIcon className="h-5 w-5" />}
        title={website?.name || 'Files'}
        description="Manage your website files"
        actions={
          <button type="button" onClick={() => navigate('/websites')} className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted/40 cursor-pointer">
            <ArrowLeftIcon className="h-4 w-4" />
            Back to Websites
          </button>
        }
      />

      {showRedeployPrompt && (
        <div className="rounded-md bg-warning/10 border border-warning/30 p-4 flex items-center justify-between">
          {website?.published ? (
            <>
              <p className="text-sm text-warning">Files changed. Redeploy to apply changes to your live site.</p>
              <div className="flex gap-2">
                <button type="button" onClick={handleRedeploy} className="inline-flex items-center rounded-md border border-transparent px-3 py-1.5 text-sm font-medium text-white bg-primary hover:bg-primary/90 cursor-pointer">Redeploy</button>
                <button type="button" onClick={() => setShowRedeployPrompt(false)} className="inline-flex items-center rounded-md border border-border px-3 py-1.5 text-sm font-medium text-foreground bg-card hover:bg-muted/40 cursor-pointer">Dismiss</button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-warning">Publish your website from the Websites page to see your modifications live.</p>
              <button type="button" onClick={() => setShowRedeployPrompt(false)} className="inline-flex items-center rounded-md border border-border px-3 py-1.5 text-sm font-medium text-foreground bg-card hover:bg-muted/40 cursor-pointer">Dismiss</button>
            </>
          )}
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        {uploading && <div className="w-full max-w-sm"><div className="text-xs text-muted-foreground mb-1">Uploading {uploadProgress}%</div><div className="h-1.5 bg-muted rounded-full"><div className="h-1.5 bg-primary rounded-full transition-all" style={{ width: `${uploadProgress}%` }} /></div></div>}
        {!uploading && (
          <>
            <button type="button" onClick={() => setShowCreateFileModal(true)} className="inline-flex items-center rounded-md border border-transparent px-3 py-1.5 text-sm font-medium text-white bg-primary hover:bg-primary/90 shadow-sm cursor-pointer">New File</button>
            <button type="button" onClick={() => setShowCreateFolderModal(true)} className="inline-flex items-center rounded-md border border-border px-3 py-1.5 text-sm font-medium text-foreground bg-card hover:bg-muted/40 shadow-sm cursor-pointer">New Folder</button>
            <button type="button" onClick={() => fileInputRef.current?.click()} className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-sm font-medium text-foreground bg-card hover:bg-muted/40 shadow-sm cursor-pointer"><ArrowUpTrayIcon className="h-4 w-4" /> Upload Files</button>
            <button type="button" onClick={() => folderInputRef.current?.click()} className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-sm font-medium text-foreground bg-card hover:bg-muted/40 shadow-sm cursor-pointer"><ArrowUpTrayIcon className="h-4 w-4" /> Upload Folder</button>
            <input ref={fileInputRef} type="file" multiple onChange={handleFileUpload} className="hidden" />
            <input ref={folderInputRef} type="file" multiple onChange={handleFolderUpload} className="hidden" {...{ webkitdirectory: '', directory: '' } as React.InputHTMLAttributes<HTMLInputElement>} />
          </>
        )}
      </div>

      {/* Breadcrumbs */}
      <nav className="flex items-center gap-1 text-sm text-muted-foreground">
        <button type="button" onClick={() => setCurrentPath('')} className="hover:text-foreground cursor-pointer">/ root</button>
        {breadcrumbs.map((crumb, i) => (
          <React.Fragment key={i}>
            <span>/</span>
            <button type="button" onClick={() => setCurrentPath(breadcrumbs.slice(0, i + 1).join('/'))} className="hover:text-foreground cursor-pointer">{crumb}</button>
          </React.Fragment>
        ))}
      </nav>

      <div className="bg-card shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/30">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Size</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Last Modified</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {currentPath && (() => {
                const parentPath = currentPath.includes('/') ? currentPath.split('/').slice(0, -1).join('/') : '';
                return (
                  <tr className="hover:bg-muted/40 cursor-pointer"
                    onClick={handleGoUp}
                    onDragOver={(e) => { if (dragItem) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; } }}
                    onDrop={(e) => handleDrop(e, parentPath)}>
                    <td colSpan={4} className="px-6 py-3 text-sm text-muted-foreground flex items-center gap-2">
                      <FolderIcon className="h-5 w-5 text-muted-foreground" /> ..
                    </td>
                  </tr>
                );
              })()}
              {currentItems.dirs.map((dir) => {
                const dirAbsPath = currentPath ? `${currentPath}/${dir}` : dir;
                return (
                <tr key={dir} className="hover:bg-muted/40"
                  draggable
                  onDragStart={(e) => { setDragItem({ type: 'folder', path: dirAbsPath }); e.dataTransfer.effectAllowed = 'move'; }}
                  onDragEnd={() => setDragItem(null)}
                  onDragOver={(e) => { if (dragItem) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; } }}
                  onDrop={(e) => handleDrop(e, dirAbsPath)}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                    <div className="flex items-center gap-2">
                      <FolderIcon className="h-5 w-5 text-yellow-400 flex-shrink-0" />
                      <button type="button" onClick={() => handleNavigate(dir)} className="font-medium hover:text-primary cursor-pointer">{dir}</button>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">—</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">—</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button type="button" onClick={() => { setFolderToRename(dir); setNewFolderNameInput(dir); setShowRenameFolderModal(true); }} className="text-primary hover:text-primary/80 mr-4 cursor-pointer" title="Rename folder"><PencilIcon className="h-5 w-5 inline" /></button>
                    <button type="button" onClick={() => handleDeleteFolder(dir)} className="text-red-600 hover:text-red-900 cursor-pointer" title="Delete folder"><TrashIcon className="h-5 w-5 inline" /></button>
                  </td>
                </tr>
                );
              })}
              {currentItems.files.map((file) => {
                const name = file.path.split('/').pop() || file.path;
                return (
                  <tr key={file.path} className="hover:bg-muted/40" draggable onDragStart={(e) => { setDragItem({ type: 'file', path: file.path }); e.dataTransfer.effectAllowed = 'move'; }} onDragEnd={() => setDragItem(null)}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                      <div className="flex items-center gap-2">
                        {getFileIcon(name)}
                        <span className="font-medium">{name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{formatFileSize(file.size)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{formatDate(file.updated_at)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button type="button" onClick={() => handleEditFile(file.path)} className="text-primary hover:text-primary/80 mr-4 cursor-pointer" title="Edit file"><PencilIcon className="h-5 w-5 inline" /></button>
                      <button type="button" onClick={() => handleDeleteFile(file.path)} className="text-red-600 hover:text-red-900 cursor-pointer" title="Delete file"><TrashIcon className="h-5 w-5 inline" /></button>
                    </td>
                  </tr>
                );
              })}
              {currentItems.dirs.length === 0 && currentItems.files.length === 0 && !currentPath && (
                <tr><td colSpan={4} className="px-6 py-8 text-center text-sm text-muted-foreground">No files yet. Upload files to get started.</td></tr>
              )}
              {currentItems.dirs.length === 0 && currentItems.files.length === 0 && currentPath && (
                <tr><td colSpan={4} className="px-6 py-8 text-center text-sm text-muted-foreground">This folder is empty.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showRedeployModal && createPortal(
        <div className="fixed inset-0 bg-background/70 backdrop-blur-sm flex items-center justify-center z-[200]">
          <div className="bg-card rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4 text-foreground">Processing...</h2>
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-6 h-6">
                {redeployStatus === 'completed' ? <svg className="w-6 h-6 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                : redeployStatus === 'error' ? <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                : <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />}
              </div>
              <div>
                <span className="font-medium text-foreground">Deploying updated content</span>
                {redeployStatus === 'completed' && redeployElapsed != null && <span className="text-muted-foreground text-sm ml-2">{(redeployElapsed / 1000).toFixed(1)}s</span>}
              </div>
            </div>
          </div>
        </div>,
      document.body
      )}

      {showCreateFileModal && createPortal(
        <div className="fixed inset-0 bg-background/70 backdrop-blur-sm flex items-center justify-center z-[200]">
          <div className="bg-card rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4 text-foreground">Create New File</h2>
            <form onSubmit={handleCreateFile}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2 text-foreground">File path</label>
                <input type="text" value={newFilePath} onChange={(e) => setNewFilePath(e.target.value)} placeholder="e.g. styles.css, css/styles.css" required
                  className="w-full px-3 py-2 border border-border rounded focus:outline-none focus:ring-2 focus-visible:ring-ring" />
                <p className="text-xs text-muted-foreground mt-1">Use slashes to create or navigate folders, e.g., <code>css/styles.css</code>.</p>
              </div>
              <div className="flex gap-2 justify-end">
                <button type="button" onClick={() => { setShowCreateFileModal(false); setNewFilePath(''); }} disabled={creatingFile} className="px-4 py-2 border border-border rounded hover:bg-muted/40 cursor-pointer">Cancel</button>
                <button type="submit" disabled={creatingFile} className="px-4 py-2 text-white rounded bg-primary hover:bg-primary/90 disabled:opacity-50 cursor-pointer">{creatingFile ? 'Creating...' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>,
      document.body
      )}

      {showCreateFolderModal && createPortal(
        <div className="fixed inset-0 bg-background/70 backdrop-blur-sm flex items-center justify-center z-[200]">
          <div className="bg-card rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4 text-foreground">Create New Folder</h2>
            <form onSubmit={handleCreateFolder}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2 text-foreground">Folder name</label>
                <input type="text" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} placeholder="e.g. css" required
                  className="w-full px-3 py-2 border border-border rounded focus:outline-none focus:ring-2 focus-visible:ring-ring" />
              </div>
              <div className="flex gap-2 justify-end">
                <button type="button" onClick={() => { setShowCreateFolderModal(false); setNewFolderName(''); }} disabled={creatingFolder} className="px-4 py-2 border border-border rounded hover:bg-muted/40 cursor-pointer">Cancel</button>
                <button type="submit" disabled={creatingFolder} className="px-4 py-2 text-white rounded bg-primary hover:bg-primary/90 disabled:opacity-50 cursor-pointer">{creatingFolder ? 'Creating...' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>,
      document.body
      )}

      {showRenameFolderModal && createPortal(
        <div className="fixed inset-0 bg-background/70 backdrop-blur-sm flex items-center justify-center z-[200]">
          <div className="bg-card rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4 text-foreground">Rename Folder</h2>
            <form onSubmit={handleRenameFolder}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2 text-foreground">New folder name</label>
                <input type="text" value={newFolderNameInput} onChange={(e) => setNewFolderNameInput(e.target.value)} placeholder="New name" required
                  className="w-full px-3 py-2 border border-border rounded focus:outline-none focus:ring-2 focus-visible:ring-ring" />
              </div>
              <div className="flex gap-2 justify-end">
                <button type="button" onClick={() => { setShowRenameFolderModal(false); setFolderToRename(null); setNewFolderNameInput(''); }} disabled={renamingFolder} className="px-4 py-2 border border-border rounded hover:bg-muted/40 cursor-pointer">Cancel</button>
                <button type="submit" disabled={renamingFolder} className="px-4 py-2 text-white rounded bg-primary hover:bg-primary/90 disabled:opacity-50 cursor-pointer">{renamingFolder ? 'Renaming...' : 'Rename'}</button>
              </div>
            </form>
          </div>
        </div>,
      document.body
      )}
    </div>
  );
}
